import { faCamera, faImages, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import useOutsideClick from "./Component/OutsideClick"; 


// Mock icons
const FaTimes = () => <span className="text-lg">×</span>;
const FaCheck = () => <span className="text-lg">✓</span>;
const FaSpinner = () => <span className="animate-spin">⟳</span>;

const UMR = ({ visible, onClose, categories = [], onRfqCreated }) => {
  const navigate = useNavigate();
  const popupRef = useRef(null);
  useOutsideClick(popupRef, onClose);
  // States
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUploadOptions, setShowUploadOptions] = useState(true);
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const fetchUrl = process.env.REACT_APP_FORE_BRICKBUCKET;
  const [statusMessage, setStatusMessage] = useState("Uploading...");
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showReviewButton, setShowReviewButton] = useState(false);
  const [showInvalidImageError, setShowInvalidImageError] = useState(false); // New state for invalid image error
  const [MR,setMR] = useState({});
   
  // AI Message Sequence with timing
 // AI Message Sequence with timing for 30 seconds
const aiMessages = [
  { time: 0, message: "📤 Uploading images...", icon: "📤" },
  { time: 2, message: "⏳ Expected time: 30 seconds", icon: "⏳" },
  { time: 5, message: "🧠 AI is analyzing materials...", icon: "🧠" },
  { time: 10, message: "🔍 AI is fetching items...", icon: "🔍" },
  { time: 15, message: "✅ AI found items!", icon: "✅" },
  { time: 20, message: "⚡ AI analysis complete!", icon: "⚡" },
  { time: 25, message: "⏰ Taking more than expected...", icon: "⏰" }
];

  // Reset states when modal closes
/*   useEffect(() => {
    if (!visible) {
      setUploadedImages([]);
      setIsSubmitting(false);
      setShowUploadOptions(true);
      setImageViewerVisible(false);
      setCurrentImageIndex(0);
      setUploadProgress(0);
      setShowSuccess(false);
      setShowReviewButton(false);
      setElapsedTime(0);
    }
  }, [visible]); */
   // Reset states when modal closes
  useEffect(() => {
    if (!visible) {
      setUploadedImages([]);
      setIsSubmitting(false);
      setShowUploadOptions(true);
      setImageViewerVisible(false);
      setCurrentImageIndex(0);
      setUploadProgress(0);
      setShowSuccess(false);
      setShowReviewButton(false);
      setElapsedTime(0);
      setShowInvalidImageError(false); // Reset error state
    }
  }, [visible]);

  // Update showUploadOptions based on uploadedImages
  useEffect(() => {
    setShowUploadOptions(uploadedImages.length === 0);
  }, [uploadedImages]);

  // Handle image upload from files
  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = (event) => {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        const newImages = files.map(file => ({
          uri: URL.createObjectURL(file),
          fileName: file.name,
          type: file.type,
          file: file,
          id: Math.random().toString(36).substr(2, 9)
        }));
        setUploadedImages(prev => [...prev, ...newImages]);
      }
    };
    
    input.click();
  };

  // Handle camera capture (simulated for web)
  const handleCameraCapture = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    
    input.onchange = (event) => {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        const newImage = {
          uri: URL.createObjectURL(files[0]),
          fileName: files[0].name,
          type: files[0].type,
          file: files[0],
          id: Math.random().toString(36).substr(2, 9)
        };
        setUploadedImages(prev => [...prev, newImage]);
      }
    };
    
    input.click();
  };

  // Generate random status progression
  const generateStatusProgression = () => {
    const statuses = ['under_review', 'project_pricing', 'estimated_value'];
    const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
    
    // Return timeline for status progression
    const timelines = {
      'under_review': {
        nextStatus: 'project_pricing',
        duration: 5000,
        description: "We're reviewing your material list to check eligibility for project pricing. You'll be notified shortly.",
        actionText: 'Processing...'
      },
      'project_pricing': {
        nextStatus: 'estimated_value', 
        duration: 8000,
        description: 'This requirement qualifies for project pricing. Request quotes directly from brands.',
        actionText: 'Request for Quotes'
      },
      'estimated_value': {
        nextStatus: null,
        duration: null,
        description: "We've created an estimated value for your material requirement. Review the items and request a final quote.",
        actionText: 'View Estimated Value'
      }
    };
    
    return {
      currentStatus: randomStatus,
      timeline: timelines[randomStatus]
    };
  };

  // Save to local storage with dynamic status
  const saveToLocalStorage = (rfqData) => {
    const existingRFQs = JSON.parse(localStorage.getItem('materialRFQs') || '[]');
    
    const statusProgression = generateStatusProgression();
    
    const newRFQ = {
      id: 'MR-' + Math.random().toString(36).substr(2, 6).toUpperCase(),
      customer_id: rfqData.customer_id,
      images: rfqData.images.map(img => ({
        uri: img.uri,
        fileName: img.fileName,
        type: img.type
      })),
      status: statusProgression.currentStatus,
      timeline: statusProgression.timeline,
      created_at: new Date().toISOString(),
      uploadedDate: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      timestamp: Date.now(),
      category_ids: rfqData.product_category_ids,
      isNew: true,
      title: 'Material Requirements',
      description: statusProgression.timeline.description,
      actionText: statusProgression.timeline.actionText
    };

    const updatedRFQs = [newRFQ, ...existingRFQs];
    localStorage.setItem('materialRFQs', JSON.stringify(updatedRFQs));
    
    if (statusProgression.timeline.nextStatus) {
      setTimeout(() => {
        progressMaterialStatus(newRFQ.id, statusProgression.timeline.nextStatus);
      }, statusProgression.timeline.duration);
    }
    
    return newRFQ;
  };

  // Progress material status
  const progressMaterialStatus = (materialId, nextStatus) => {
    const storedMaterials = JSON.parse(localStorage.getItem('materialRFQs') || '[]');
    const updatedMaterials = storedMaterials.map(material => {
      if (material.id === materialId) {
        let newDescription = '';
        let newActionText = '';
        let nextNextStatus = null;
        let nextDuration = null;

        switch (nextStatus) {
          case 'project_pricing':
            newDescription = 'This requirement qualifies for project pricing. Request quotes directly from brands.';
            newActionText = 'Request for Quotes';
            nextNextStatus = 'estimated_value';
            nextDuration = 8000;
            break;
          case 'estimated_value':
            newDescription = "We've created an estimated value for your material requirement. Review the items and request a final quote.";
            newActionText = 'View Estimated Value';
            nextNextStatus = null;
            nextDuration = null;
            break;
          default:
            newDescription = material.description;
            newActionText = material.actionText;
        }

        const updatedMaterial = {
          ...material,
          status: nextStatus,
          description: newDescription,
          actionText: newActionText,
          timeline: {
            nextStatus: nextNextStatus,
            duration: nextDuration
          }
        };

        if (nextNextStatus && nextDuration) {
          setTimeout(() => {
            progressMaterialStatus(materialId, nextNextStatus);
          }, nextDuration);
        }

        return updatedMaterial;
      }
      return material;
    });

    localStorage.setItem('materialRFQs', JSON.stringify(updatedMaterials));
    
    if (onRfqCreated) {
      onRfqCreated({ id: materialId, status: nextStatus });
    }
  };

  // Get current AI message based on elapsed time
  const getCurrentAIMessage = (time) => {
    // Find the most recent message that should be shown
    const currentMessage = [...aiMessages]
      .reverse()
      .find(msg => time >= msg.time);
    
    return currentMessage || aiMessages[0];
  };

  // Handle save action
   // Handle save action
  const handleSave = async () => {
    if (uploadedImages.length === 0) {
      alert("Error: Please upload at least one image");
      return;
    }

    try {
      setIsSubmitting(true);
      setShowReviewButton(false);
      setShowInvalidImageError(false); // Reset error state
      setElapsedTime(0);
      setUploadProgress(0);

      const startTime = Date.now();
      let responseReceived = false;

      // Timer for UI updates - updated for 30 seconds max
      const timer = setInterval(() => {
        setElapsedTime((prev) => {
          const newTime = prev + 1;
          const currentMessage = getCurrentAIMessage(newTime);
          setStatusMessage(currentMessage.message);
          
          // Update progress bar based on time (max 95% until response) - 30 seconds total
          const progress = Math.min(95, Math.round((newTime / 30) * 95));
          setUploadProgress(progress);
          
          return newTime;
        });
      }, 1000);

      // Start the actual API call
      const apiCall = async () => {
        try {
          const userData = JSON.parse(sessionStorage.getItem("userData"));
          const userId = userData?._id;
          const token = sessionStorage.getItem("token");

          if (!userId) {
            throw new Error("User not found. Please log in again.");
          }

          const formData = new FormData();
          formData.append("customer_id", userId);
          uploadedImages.forEach((img) => {
            if (img.file) formData.append("images", img.file);
          });

          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 90000); // 30 seconds timeout

          const response = await fetch(
            `${fetchUrl}customer/material-requirement/create-or-update-material-requirement`,
            {
              method: "POST",
              headers: { "x-token": token },
              body: formData,
              signal: controller.signal
            }
          );
          
          console.log("Response:", response);
          clearTimeout(timeoutId);

          const result = await response.json();
          console.log("API Result:", result);

          setMR(result.data)

          // Check for invalid image error (400 status)
          if (response.status === 400 && result.message?.includes("Invalid image")) {
            throw new Error("INVALID_IMAGE");
          }

          if (!response.ok) {
            throw new Error(result.message || "Failed to save material requirement");
          }

          return result;
        } catch (error) {
          throw error;
        }
      };

      // Handle API response
      apiCall().then((result) => {
        responseReceived = true;
        clearInterval(timer);
        
        const responseTime = Date.now() - startTime;
        const responseSeconds = Math.floor(responseTime / 1000);
        
        console.log(`✅ AI Analysis completed in ${responseSeconds} seconds`);
        // Show completion and enable review button
        setStatusMessage("🎉 Analysis Complete! Review items now");
        setUploadProgress(100);
        setShowReviewButton(true);
        
      }).catch((error) => {
        responseReceived = true;
        clearInterval(timer);
        console.error("Error in handleSave:", error);
        
        // Handle invalid image error specifically
        if (error.message === "INVALID_IMAGE") {
          setStatusMessage("❌ Invalid Image Detected");
          setUploadProgress(0);
          setShowInvalidImageError(true); // Show error in UI
          setIsSubmitting(false); // Allow user to upload again
        } else {
          // Handle other errors
          setStatusMessage("❌ Analysis Failed");
          setUploadProgress(0);
          
          setTimeout(() => {
            setIsSubmitting(false);
            alert("Error: " + (error.message || "Unable to save material requirement"));
          }, 1000);
        }
      });

      // Timeout fallback - updated to 31 seconds
      setTimeout(() => {
        if (!responseReceived) {
          clearInterval(timer);
          setIsSubmitting(false);
          setStatusMessage("⏰ Request timeout. Please try again.");
          alert("Request timeout. Please try again.");
        }
      }, 31000);

    } catch (error) {
      console.error("Error in handleSave setup:", error);
      setIsSubmitting(false);
      alert("Error: Unable to process request");
    }
  };

  // Handle review items action
  const handleReviewItems = () => {
    setShowSuccess(true);
        navigate("/consumer/app/reviewitemlist", {
      state: {
        materialRequirementId: MR._id,
        materialItemId:MR.mri_id,
        //buttonType: actionType,
        MR_images: MR.material_list_attachments,
        mrId: MR._id,
        }
      });
/*     try {
      const userData = JSON.parse(sessionStorage.getItem("userData"));
      const rfqData = {
        customer_id: userData?._id,
        images: uploadedImages.map(img => ({
          uri: img.uri,
          fileName: img.fileName,
          type: img.type
        })),
        product_category_ids: []
      };
      
      saveToLocalStorage(rfqData);
      
      setTimeout(() => {
        onClose();
        setUploadedImages([]);
        setIsSubmitting(false);
        setShowSuccess(false);
        setShowReviewButton(false);
        setUploadProgress(0);
        navigate("/consumer/app/materiallist"); 
        if (onRfqCreated) onRfqCreated({});
      }, 1500);
      
    } catch (error) {
      console.error("Error in success handling:", error);
      setTimeout(() => {
        onClose();
        setIsSubmitting(false);
        navigate("/consumer/app/materiallist");
      }, 1500);
    } */
  };

  // Remove uploaded image
  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Show upload options
  const showUploadOptionsSection = () => {
    setShowUploadOptions(true);
  };

  // Open image viewer
  const openImageViewer = (index) => {
    setCurrentImageIndex(index);
    setImageViewerVisible(true);
  };

  // Close image viewer
  const closeImageViewer = () => {
    setImageViewerVisible(false);
  };

  // Navigate through images in viewer
  const goToNextImage = () => {
    setCurrentImageIndex(prev => 
      prev < uploadedImages.length - 1 ? prev + 1 : prev
    );
  };

  const goToPrevImage = () => {
    setCurrentImageIndex(prev => prev > 0 ? prev - 1 : prev);
  };

  // Handle keyboard navigation in image viewer
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!imageViewerVisible) return;
      
      if (e.key === 'ArrowLeft') {
        goToPrevImage();
      } else if (e.key === 'ArrowRight') {
        goToNextImage();
      } else if (e.key === 'Escape') {
        closeImageViewer();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [imageViewerVisible, currentImageIndex, uploadedImages.length]);

  // Reset and show upload options when invalid image error occurs
  const handleRetryUpload = () => {
    setShowInvalidImageError(false);
    setShowUploadOptions(true);
    setUploadedImages([]); // Clear invalid images
  };


  if (!visible) return null;

  return (
    <>
<div className="fixed inset-0 bg-black bg-opacity-40 flex items-end justify-end z-50 md:items-center md:justify-center">
   <div 
    ref={popupRef}

    className="bg-white rounded-t-3xl w-full max-h-[90vh] overflow-hidden md:rounded-2xl md:max-w-md md:max-h-[80vh]">
       {/* Header */} 
       <div className="px-5 pt-5 pb-4 border-b border-gray-300"> 
        <div className="flex flex-row justify-between items-center">
           <h2 className="text-lg font-semibold text-gray-900">
             {showSuccess ? 'Upload Complete!' : 'Upload Material Requirement'} 
             </h2> 
             {!showSuccess && !isSubmitting &&
              ( 
                   <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-3xl"
        >
          &times;
        </button>
                 )}
                   </div>
                    </div>

          <div className="px-5 py-4 max-h-[calc(90vh-80px)] overflow-y-auto">
            
            {/* Invalid Image Error State */}
            {showInvalidImageError ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <FontAwesomeIcon className='text-2xl' icon={faTimes} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                  Invalid Image Detected
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  The image you uploaded couldn't be processed. Please upload a clear, valid image of construction materials.
                </p>
                
                {/* Upload Options for Retry */}
                <div className="w-full mb-6">
                  <button
                    onClick={handleCameraCapture}
                    className="flex flex-row items-center p-5 rounded-xl border border-gray-200 mb-3 w-full hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                      <FontAwesomeIcon icon={faCamera} />
                    </div>
                    <div className="flex-1 text-left">
                      <span className="text-sm font-medium text-gray-900 block">Take Photo Again</span>
                      <span className="text-xs text-gray-500 block">Use your camera to capture a clear image</span>
                    </div>
                  </button>

                  <button
                    onClick={handleImageUpload}
                    className="flex flex-row items-center p-5 rounded-xl border border-gray-200 w-full hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                      <FontAwesomeIcon icon={faImages} className='text-xl' />
                    </div>
                    <div className="flex-1 text-left">
                      <span className="text-sm font-medium text-gray-900 block">Choose Different Image</span>
                      <span className="text-xs text-gray-500 block">Select a valid photo from your device</span>
                    </div>
                  </button>
                </div>

                {/* <button
                  onClick={handleRetryUpload}
                  className="w-full py-4 rounded-lg bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors"
                >
                  Start Over
                </button> */}
              </div>
            ) : showSuccess ? (
              // Success State (existing code)
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <FaCheck className="text-green-600 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Requirement Submitted!
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  Your material requirement has been uploaded successfully and is now being processed.
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-500">
                  Redirecting to materials list...
                </p>
              </div>
            ) : (
              /* Normal Flow */
              <>
                {/* Upload Options Section */}
                {showUploadOptions ? (
                  <div>
                    <p className="text-sm text-gray-600 mb-2 text-center">
                      {uploadedImages.length > 0 ? 'Add more photos' : 'Add material requirement photo(s)'}
                    </p>
                    <p className="text-xs text-gray-500 mb-6 text-center">
                      {uploadedImages.length > 0 
                        ? 'Add more images to your requirement' 
                        : 'Upload clear images of construction materials you need quotes for'
                      }
                    </p>

                    {/* Upload Options */}
                    <div className="mb-6">
                      <button
                        onClick={handleCameraCapture}
                        className="flex flex-row items-center p-5 rounded-xl border border-gray-200 mb-3 w-full hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                          <FontAwesomeIcon icon={faCamera} />
                        </div>
                        <div className="flex-1 text-left">
                          <span className="text-sm font-medium text-gray-900 block">Take Photo</span>
                          <span className="text-xs text-gray-500 block">Use your camera to capture clear images</span>
                        </div>
                      </button>

                      <button
                        onClick={handleImageUpload}
                        className="flex flex-row items-center p-5 rounded-xl border border-gray-200 w-full hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                         <FontAwesomeIcon icon={faImages} className='text-xl' />
                        </div>
                        <div className="flex-1 text-left">
                          <span className="text-sm font-medium text-gray-900 block">Choose from Gallery</span>
                          <span className="text-xs text-gray-500 block">Select clear photos from your device</span>
                        </div>
                      </button>
                    </div>

                    {/* Show "Back to Review" button if we have existing images */}
                    {uploadedImages.length > 0 && (
                      <button
                        onClick={() => setShowUploadOptions(false)}
                        className="w-full py-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                      >
                        Back to Review
                      </button>
                    )}
                  </div>
                ) : (
                  /* Review & Submit Section */
                  <div>
                    <p className="text-sm text-gray-600 mb-2 text-center">
                      {showReviewButton ? 'Analysis Complete!' : 'Review your upload before submitting'}
                    </p>
                    {!isSubmitting && !showReviewButton && (
                      <p className="text-xs text-gray-500 mb-6 text-center">
                        {uploadedImages.length} photo(s) uploaded
                      </p>
                    )}

                    {/* Upload Progress */}
                    {isSubmitting && (
                      <div className="flex flex-col items-center justify-center my-8">
                        <div className="relative w-24 h-24 mb-4">
                          <svg className="transform -rotate-90 w-24 h-24">
                            <circle
                              cx="48"
                              cy="48"
                              r="40"
                              stroke="currentColor"
                              strokeWidth="8"
                              className="text-gray-200"
                              fill="transparent"
                            />
                            <circle
                              cx="48"
                              cy="48"
                              r="40"
                              stroke="currentColor"
                              strokeWidth="8"
                              className="text-blue-600 transition-all duration-300"
                              fill="transparent"
                              strokeDasharray="251.2"
                              strokeDashoffset={251.2 - (uploadProgress / 100) * 251.2}
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center flex-col">
                            <span className="text-lg font-bold text-gray-700">
                              {uploadProgress}%
                            </span>
                          </div>
                        </div>

                        <p className="text-lg font-medium text-gray-800 mt-3 text-center min-h-[28px]">
                          {statusMessage}
                        </p>
                      </div>
                    )}

                    {/* Uploaded Images */}
                    <div className="mb-6">
                      <div className="flex flex-row justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-900">Photos</span>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <div className="flex flex-row gap-3 pb-2">
                          {uploadedImages.map((img, idx) => (
                            <div key={img.id} className="relative flex-shrink-0">
                              <button
                                onClick={() => openImageViewer(idx)}
                                className="rounded-lg overflow-hidden bg-gray-100 hover:opacity-80 transition-opacity"
                              >
                                <img
                                  src={img.uri}
                                  alt={`Upload ${idx + 1}`}
                                  className="w-20 h-20 object-cover"
                                />
                              </button>
                              <button
                                onClick={() => removeImage(idx)}
                                className="absolute -top-2 -right-2 bg-gray-800 rounded-full w-5 h-5 flex items-center justify-center text-white hover:bg-gray-700 transition-colors"
                                disabled={isSubmitting}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-row gap-3">
                      <button
                        onClick={showUploadOptionsSection}
                        disabled={isSubmitting}
                        className="flex-1 py-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add More Photos
                      </button>
                      
                      {showReviewButton ? (
                        <button
                          onClick={handleReviewItems}
                          className="flex-1 py-4 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <FaCheck />
                          Review Items
                        </button>
                      ) : (
                        <button
                          onClick={handleSave}
                          disabled={isSubmitting}
                          className={`flex-1 py-4 rounded-lg bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 ${
                            isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {isSubmitting ? (
                            <>
                              <FaSpinner />
                              Processing...
                            </>
                          ) : (
                            'Submit'
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Image Viewer Modal */}
      {imageViewerVisible && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          {/* Header */}
          <div className="absolute top-8 left-0 right-0 z-10 px-4">
            <div className="flex flex-row justify-end items-center">
              <button 
                onClick={closeImageViewer}
                className="bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center text-white hover:bg-opacity-70 transition-colors"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Navigation Arrows */}
          {currentImageIndex > 0 && (
            <button 
              onClick={goToPrevImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center text-white text-2xl hover:bg-opacity-70 transition-colors z-10"
            >
              ‹
            </button>
          )}
          
          {currentImageIndex < uploadedImages.length - 1 && (
            <button 
              onClick={goToNextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center text-white text-2xl hover:bg-opacity-70 transition-colors z-10"
            >
              ›
            </button>
          )}

          {/* Image */}
          <div className="flex items-center justify-center w-full h-full p-8">
            <img
              src={uploadedImages[currentImageIndex]?.uri}
              alt={`Viewer ${currentImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {/* Footer with counter */}
          <div className="absolute bottom-8 left-0 right-0 flex justify-center z-10">
            <span className="text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded-full">
              {currentImageIndex + 1} / {uploadedImages.length}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default UMR;